import { useEffect, useId, useRef, type RefObject } from 'react';

/**
 * What every overlay (ModalShell, Sheet, the command palette) needs, in one
 * place:
 *
 *  - Escape closes only the top-most layer: with a Sheet over a modal, one
 *    Escape used to close both.
 *  - Focus moves into the dialog when it opens, Tab stays inside it, and
 *    focus returns to whatever opened it when it closes (it used to land on
 *    <body>, and Tab walked out into the page behind).
 *  - The body scroll lock is reference-counted, so nested overlays no longer
 *    restore `overflow` out of order and leave the page locked.
 */

const stack: string[] = [];
/** The named layers on the stack (a layer that passed a `layer` name). */
const names = new Map<string, string>();
let lockCount = 0;
let lockedOverflow = '';

/** The command palette's layer name. */
export const PALETTE_LAYER = 'command-palette';

/**
 * True while any dialog layer is open (the palette's Cmd+K checks it).
 * `except` leaves one named layer out: the palette is still on the stack for
 * the moment its exit animation runs, and Cmd+K pressed then must not count
 * it as a dialog over the page (F35: Ctrl K did not reopen it).
 */
export function isDialogOpen(except?: string): boolean {
  return stack.some((id) => except === undefined || names.get(id) !== except);
}

const FOCUSABLE =
  'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), iframe, [contenteditable="true"], [tabindex]:not([tabindex="-1"])';

function focusables(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (el) => !el.hasAttribute('inert') && el.getAttribute('aria-hidden') !== 'true',
  );
}

function lockScroll(): void {
  if (lockCount === 0) {
    lockedOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }
  lockCount += 1;
}

function unlockScroll(): void {
  lockCount = Math.max(0, lockCount - 1);
  if (lockCount === 0) document.body.style.overflow = lockedOverflow;
}

export function useDialogLayer(open: boolean, onClose: () => void, ref: RefObject<HTMLElement | null>, layer?: string): void {
  const id = useId();
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open || typeof document === 'undefined') return;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    stack.push(id);
    if (layer) names.set(id, layer);
    lockScroll();

    // After the dialog has rendered: the first field or button, else the panel itself.
    const raf = requestAnimationFrame(() => {
      const root = ref.current;
      if (!root || root.contains(document.activeElement)) return;
      const first = focusables(root)[0];
      if (first) first.focus();
      else {
        if (!root.hasAttribute('tabindex')) root.setAttribute('tabindex', '-1');
        root.focus();
      }
    });

    const onKey = (e: KeyboardEvent): void => {
      if (stack[stack.length - 1] !== id) return;
      if (e.key === 'Escape') {
        e.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (e.key !== 'Tab') return;
      const root = ref.current;
      if (!root) return;
      const items = focusables(root);
      if (items.length === 0) {
        e.preventDefault();
        return;
      }
      const first = items[0]!;
      const last = items[items.length - 1]!;
      const active = document.activeElement;
      if (e.shiftKey && (active === first || !root.contains(active))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (active === last || !root.contains(active))) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);

    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('keydown', onKey);
      const at = stack.lastIndexOf(id);
      if (at >= 0) stack.splice(at, 1);
      names.delete(id);
      unlockScroll();
      if (opener && opener.isConnected) opener.focus();
    };
  }, [open, id, ref, layer]);
}
